/* eslint-disable max-lines */

import React, { useState, useRef } from 'react';
import { RootReducer } from 'store/root-reducer';
import {
  getCategoryId,
  getDivisionId,
  getIsTintServiceAvailableForProduct,
  getSubCategoryId,
  getSubDivisionId,
  isMainInfoLoading,
  isVariantsLoading,
  isWebSellable,
} from 'features/product-info';
import { Skeleton } from 'fronton-react/unstable';
import { useBreakpoints } from 'common/hooks/use-breakpoints';
import {
  isWebAvailable as getIsWebAvailable,
  getMaxStockAvailable,
  getStocksInStores,
} from 'features/goods-supply';
import { connect, useSelector } from 'react-redux';
import { ConnectedProps } from 'types/utils';
import { jcm } from 'utils';
import { useIsomorphicLayoutEffect } from '@navi-toolbox/hooks';
import { styles } from 'features/product-info/components/pdp-showcase/styles';
import { PdpReviewsTotalScore } from 'features/product-info/components/pdp-reviews-total-score';
import { PdpVariants } from 'features/product-info/components/pdp-variants';
import { PdpFloorCoveringsDescription } from 'features/product-info/components/pdp-floor-coverings';
import { StickyCardPanel } from 'features/product-info/components/pdp-sticky-card-panel/pdp-sticky-card-panel';
import { PdpCalculateQuantity } from 'features/product-info/components/pdp-calculate-quantity';
import { PdpMeasurementHint } from 'features/product-info/components/measurement-hint';
import { PdpOnlineOrderOnly } from 'features/product-info/components/pdp-online-order-only';
import {
  isVariantsFetched,
  getProductId,
  isOutOfStockPriceDisplayed,
} from 'features/product-info/ducks/selectors';
import { RelatedServices } from 'features/related-services';
import {
  PdpStocksInStores,
  StocksInStoresHidden,
} from 'features/product-info/components/pdp-stocks-in-stores';
import { PdpAvailabilityToBuy } from 'features/product-info/components/pdp-availability-to-buy';
import {
  PdpDeliveryMethods,
  PdpDeliverySEOStub,
} from 'features/product-info/components/pdp-delivery-methods';
import { ProductReturns } from 'features/product-returns';
import { getNeedRenderFloorCoveringsComponents } from 'features/product-info/components/pdp-showcase/ducks/selectors';
import { getIsBot, getIsSetPath } from 'common/selectors';
import { areReviewsLoading } from 'features/reviews/selectors';
import {
  getHideReturn,
  getHideBoxM2Suggest,
  getIsTintServiceAvailableInRegion,
  getAllowComparisons,
  getCatalogue4LevelLink,
} from 'features/page-model';
import { getRegionName } from 'features/page-model/ducks/get-model';
import { PdpBoosterButton } from 'features/product-info/components/pdp-booster-button';
import { SetBlock, SetOutOfStock } from 'features/product-info/components/set-block';
import { hasStock } from 'features/product-info/utils';
import { Containers, reviewsSkeleton, sticky, variantsSkeleton, setOfStock } from '../styles';
import { ProductReturnsModalContent } from 'features/product-returns/components';
import { ProductTitle } from '../product-title';
import { WishlistButton } from '../../../wishlist-button';
import { PdpComparison } from '../../../pdp-comparison';
import { SnackbarProvider } from 'fronton-react';
import { PdpPriceView } from 'features/product-info/components/pdp-price-view';
import { getCartItem } from 'features/basket';
import { measurementHintMarginTop } from 'features/product-info/components/measurement-hint/styles';

export const DATA_QA_SHOWCASE = 'pdp-showcase';
export const DATA_QA_VARIANTS = 'variants_mf-pdp';
export const DATA_QA_MEASUREMENT = 'measurement-data_mf-pdp';
export const DATA_QA_ONLINE_ORDER_ONLY = 'online-order-only_mf-pdp';
export const DATA_QA_STOCKS = 'stocks_mf-pdp';
export const DATA_QA_AVAILABILITY = 'availability_mf-pdp';
export const DATA_QA_DELIVERY_METHODS = 'delivery-methods_mf-pdp';
export const DATA_QA_REVIEW_SCORE = 'reviews-score_mf-pdp';
export const DATA_QA_SET_OUT_OF_STOCK = 'set-out-of-stock_mf-pdp';

const TOP_POSITION = 24;
const MARGIN = TOP_POSITION * 2;

export type ProductInfoContentProps = ConnectedProps<typeof mapStoreToProps> & { className?: string };

export const ProductInfoContent: React.FC<ProductInfoContentProps> = ({
  isWebAvailable,
  isSellable,
  productDataIsLoading,
  needRenderFloorCoveringsComponents,
  isBot,
  divisionId,
  subDivisionId,
  categoryId,
  subCategoryId,
  className,
  isVariants,
  isVariantsInfoLoading,
  isReviewInfoLoading,
  shouldShowTintService,
  regionName,
}) => {
  const { desktop, largeDesktop } = useBreakpoints() || {
    desktop: true,
    largeDesktop: true,
  };
  const isDesktop = desktop || largeDesktop;
  const ref = useRef<HTMLDivElement | null>(null);
  const position = useRef(0);
  const [isVisible, setIsVisible] = useState(true);
  const isHideReturn = useSelector(getHideReturn);
  const isBoxM2SuggestHidden = useSelector(getHideBoxM2Suggest);
  const allowComparison = useSelector(getAllowComparisons);
  const catalogue4LevelLink = useSelector(getCatalogue4LevelLink);
  const isSet = useSelector(getIsSetPath);
  const stocks = useSelector(getStocksInStores);
  const productId = useSelector(getProductId);
  const cartEntity = useSelector(getCartItem(productId || ''));
  const outOfStockPriceDisplayed = useSelector(isOutOfStockPriceDisplayed);
  const maxStockAvailable = useSelector(getMaxStockAvailable);
  const inBasket = !!cartEntity;
  const quantity = cartEntity?.quantity || 0;

  function onScroll() {
    if (!isVisible && window.scrollY < position.current) setIsVisible(true);
    if (isVisible && window.scrollY > position.current) setIsVisible(false);
  }

  useIsomorphicLayoutEffect(() => {
    if (isDesktop) {
      window.addEventListener('scroll', onScroll);
      return () => {
        window.removeEventListener('scroll', onScroll);
      };
    }
  }, [ref, isVisible, isDesktop, onScroll]);

  useIsomorphicLayoutEffect(() => {
    if (isDesktop && ref.current) {
      position.current = ref.current.clientHeight + MARGIN;
      if (window.scrollY > position.current) setIsVisible(false);
    }
  }, [
    ref,
    isWebAvailable,
    isSellable,
    isVariants,
    isDesktop,
    productDataIsLoading,
    needRenderFloorCoveringsComponents,
  ]);

  const isEligibleProduct = (isWebAvailable && isSellable) || isBot;
  const isEligibleSet = (isSet && (hasStock(stocks) || maxStockAvailable > 0)) || isBot;
  const isEligible = isSet ? isEligibleSet : isEligibleProduct;
  const showVariants = isEligible && isVariants;

  return (
    <Containers.ProductInfo
      className={jcm({
        [className as string]: true,
        [sticky]: !isVisible,
      })}
      ref={ref}
      data-qa={DATA_QA_SHOWCASE}
    >
      <ProductTitle productDataIsLoading={productDataIsLoading} />
      {!isSet && (
        <>
          <Containers.ReviewsScore data-qa={DATA_QA_REVIEW_SCORE}>
            {isReviewInfoLoading ? <Skeleton className={reviewsSkeleton} /> : <PdpReviewsTotalScore />}
          </Containers.ReviewsScore>
          <Containers.AddToLists>
            <WishlistButton productDataIsLoading={productDataIsLoading} />
            {allowComparison && (
              <SnackbarProvider>
                <PdpComparison />
              </SnackbarProvider>
            )}
          </Containers.AddToLists>
        </>
      )}
      <Containers.SkeletonRows>
        {isVariantsInfoLoading ? (
          <Skeleton className={variantsSkeleton} />
        ) : (
          showVariants && (
            <Containers.ProductVariants data-qa={DATA_QA_VARIANTS}>
              <PdpVariants className={styles.productVariants} />
            </Containers.ProductVariants>
          )
        )}
        {isSet ? (
          <SetBlock />
        ) : (
          <>
            {needRenderFloorCoveringsComponents ? <PdpFloorCoveringsDescription /> : undefined}
            <StickyCardPanel
              shouldShowTintService={shouldShowTintService}
              productDataIsLoading={productDataIsLoading}
              outOfStockPriceDisplayed={outOfStockPriceDisplayed}
            />
            {isEligible && !isBoxM2SuggestHidden && (
              <div className={measurementHintMarginTop} data-qa={DATA_QA_MEASUREMENT}>
                <PdpMeasurementHint />
              </div>
            )}
            <PdpBoosterButton />
            {isEligible && <PdpCalculateQuantity />}
            {isEligible && !productDataIsLoading && (
              <Containers.OnlineOrderOnly data-qa={DATA_QA_ONLINE_ORDER_ONLY}>
                <PdpOnlineOrderOnly />
              </Containers.OnlineOrderOnly>
            )}
            <PdpDeliverySEOStub regionName={regionName} />
          </>
        )}
        {isEligible && <RelatedServices isVisible={isVisible} />}
        {isEligible && (
          <div data-qa={DATA_QA_STOCKS}>
            {!isBot ? <PdpStocksInStores isVisible={isVisible} isSet={isSet} /> : null}
          </div>
        )}
        <StocksInStoresHidden />
      </Containers.SkeletonRows>
      {!isSet && !isEligible && (
        <>
          <PdpPriceView isStickyPanel={false} inBasket={inBasket} quantity={quantity} />
          <Containers.AvailabilityMark data-qa={DATA_QA_AVAILABILITY}>
            <PdpAvailabilityToBuy />
          </Containers.AvailabilityMark>
        </>
      )}
      {isEligible && (
        <>
          <Containers.DeliveryMethods data-qa={DATA_QA_DELIVERY_METHODS}>
            {!isBot && <PdpDeliveryMethods stocks={stocks} isVisible={isVisible} isSet={isSet} />}
          </Containers.DeliveryMethods>
          {!isHideReturn?.on && (
            <ProductReturns
              active={isEligibleSet}
              division={divisionId}
              subDivision={subDivisionId}
              category={categoryId}
              subCategory={subCategoryId}
              isSet={isSet}
            />
          )}
        </>
      )}
      {isSet && !isEligibleSet && (
        <div className={setOfStock} data-qa={DATA_QA_SET_OUT_OF_STOCK}>
          <SetOutOfStock link={catalogue4LevelLink} />
        </div>
      )}
      {/* TODO: NAVI-5095 Необходимо рендерить для SEO */}
      <ProductReturnsModalContent hidden />
    </Containers.ProductInfo>
  );
};

export const mapStoreToProps = (store: RootReducer) => ({
  productDataIsLoading: isMainInfoLoading(store) ?? false,
  needRenderFloorCoveringsComponents: getNeedRenderFloorCoveringsComponents(store),
  isBot: getIsBot(store),
  divisionId: getDivisionId(store) || '',
  subDivisionId: getSubDivisionId(store) || '',
  categoryId: getCategoryId(store) || '',
  subCategoryId: getSubCategoryId(store) || '',
  isWebAvailable: getIsWebAvailable(store),
  isSellable: isWebSellable(store),
  isVariants: isVariantsFetched(store),
  isVariantsInfoLoading: isVariantsLoading(store),
  isReviewInfoLoading: areReviewsLoading(store),
  regionName: getRegionName(store) ?? '',
  shouldShowTintService:
    getIsTintServiceAvailableInRegion(store) && getIsTintServiceAvailableForProduct(store),
});

export const ProductInfoContentConnected = connect(mapStoreToProps)(ProductInfoContent);
